import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Typography, Arrange, Spacer } from "@aviary";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useHasCatalogSearchShared } from "@shared/hooks/useHasCatalogSearchShared/useHasCatalogSearchShared";

import { FilterButton, FilterButtonStyle } from "../../CatalogSearch/FilterButton/FilterButton";
import { CatalogSortDropdown } from "../../CatalogSortDropdown/CatalogSortDropdown";
import * as styles from "../ProductResultsHeader.styles";

import { SkeletonEmersonProductResultsHeader } from "./SkeletonEmersonProductResultsHeader";

interface Props {
  title: string | (ReactElement | string)[];
  resultCount?: number;
  isLoadingNewResults?: boolean;
  onClearFilters?: () => void;
}

const EmersonProductResultsHeader = ({
  title,
  resultCount,
  isLoadingNewResults,
  onClearFilters,
}: Props) => {
  const { t } = useTranslation("common");
  const hasSearch = useHasCatalogSearchShared();

  const { tablet } = useBreakpoints();
  const isMobile = tablet.lessThan;

  const { searchQuery, typeaheadSelectionName } = useCatalogSearch();
  const isSortViewSelectorDisabled = !resultCount || resultCount <= 1;
  const query = searchQuery || typeaheadSelectionName;

  if (isLoadingNewResults) {
    return <SkeletonEmersonProductResultsHeader />;
  }

  const renderSortAndFilterButtons = () => {
    return (
      <>
        <Arrange
          justify="spaceBetween"
          css={styles.margin}
          isVertical={isMobile}
          isReversed={isMobile}
          isFullWidth={isMobile}
        >
          <FilterButton type={FilterButtonStyle.TEXT_AND_ICON} onClearFilters={onClearFilters} />
          <CatalogSortDropdown
            isEmerson
            isFullWidth={isMobile}
            isTransparent={false}
            isDisabled={isSortViewSelectorDisabled}
          />
        </Arrange>
        <Spacer height="one" />
      </>
    );
  };

  const renderSearchQuery = () => {
    if (!hasSearch) {
      return (
        <Typography type="h3" isMarginless>
          {t("PopularProducts")}
        </Typography>
      );
    }

    if (query) {
      return (
        <Typography type="h3" isMarginless>
          &ldquo;{query}&rdquo;
        </Typography>
      );
    }

    return null;
  };

  return (
    <div>
      <Arrange spacing={isMobile ? "xsmall" : "medium"} isVertical>
        {renderSearchQuery()}
        <Typography type="body" isMarginless data-e2e="product-results-quantity">
          {title}
        </Typography>
      </Arrange>
      <div>{renderSortAndFilterButtons()}</div>
    </div>
  );
};

export { EmersonProductResultsHeader };
